/**
 * BHI Dashboard — date-range presets.
 *
 * The backend takes a single inclusive [from, to] window. UI chips
 * are ordered by how often billing/health folks reach for them
 * when reviewing health metrics — retrospective ("previous")
 * windows first because analysis is almost always post-hoc:
 *
 *   previousMonth   — full previous calendar month (default; the
 *                     killer preset for monthly reviews)
 *   currentMonth    — 1st of this month → today
 *   previousQuarter — full previous calendar quarter
 *   currentQuarter  — 1st of this quarter → today
 *   ytd             — Jan 1 of this year → today
 *   fullYear        — Jan 1 of specified year → Dec 31 of that year
 *   custom          — user-picked range
 *
 * Quarter / month / year pickers under the chip popovers drop back
 * to `custom` when the user picks a specific past window — the
 * preset id only tracks the *shape* of the current selection.
 */
import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
} from 'date-fns';

export const PRESET_IDS = {
  custom: 'custom',
  previousMonth: 'previousMonth',
  currentMonth: 'currentMonth',
  previousQuarter: 'previousQuarter',
  currentQuarter: 'currentQuarter',
  ytd: 'ytd',
  fullYear: 'fullYear',
};

/**
 * Display order for the preset popover — retrospective windows
 * first because that's what customers actually pick. `custom` is
 * the chip's own edit affordance, not a menu row, so it's excluded.
 */
export const PRESET_ORDER = [
  PRESET_IDS.previousMonth,
  PRESET_IDS.currentMonth,
  PRESET_IDS.previousQuarter,
  PRESET_IDS.currentQuarter,
  PRESET_IDS.ytd,
  PRESET_IDS.fullYear,
];

export const buildPresetRange = (presetId, today = new Date()) => {
  if (presetId === PRESET_IDS.previousMonth) {
    const prev = subMonths(today, 1);
    return { startDate: startOfMonth(prev), endDate: endOfMonth(prev) };
  }
  if (presetId === PRESET_IDS.currentMonth) {
    return { startDate: startOfMonth(today), endDate: today };
  }
  if (presetId === PRESET_IDS.previousQuarter) {
    const prev = subQuarters(today, 1);
    return { startDate: startOfQuarter(prev), endDate: endOfQuarter(prev) };
  }
  if (presetId === PRESET_IDS.currentQuarter) {
    return { startDate: startOfQuarter(today), endDate: today };
  }
  if (presetId === PRESET_IDS.ytd) {
    return { startDate: startOfYear(today), endDate: today };
  }
  if (presetId === PRESET_IDS.fullYear) {
    return { startDate: startOfYear(today), endDate: endOfYear(today) };
  }
  return null;
};

const isoDay = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Encode a `{startDate, endDate}` pair for the API. Backend expects
 * inclusive ISO YYYY-MM-DD; returns null if either endpoint is
 * missing (caller skips the fetch in that case).
 */
export const formatRangeForApi = (range) => {
  if (!range?.startDate || !range?.endDate) return null;
  return { from: isoDay(range.startDate), to: isoDay(range.endDate) };
};

/**
 * Full-quarter window `{start, end}` for a given year + quarter
 * index (1-4). Used by the quarter-picker popover.
 */
export const buildQuarterRange = (year, quarter) => {
  const anchor = new Date(year, (quarter - 1) * 3, 1);
  return { startDate: startOfQuarter(anchor), endDate: endOfQuarter(anchor) };
};

/**
 * Full-month window `{start, end}` for a given year + month index
 * (0-11). Used by the month-picker popover.
 */
export const buildMonthRange = (year, monthIndex) => {
  const anchor = new Date(year, monthIndex, 1);
  return { startDate: startOfMonth(anchor), endDate: endOfMonth(anchor) };
};

/**
 * Full-year window `{start, end}` for a given year. Used by the
 * year-picker popover.
 */
export const buildYearRange = (year) => {
  const anchor = new Date(year, 0, 1);
  return { startDate: startOfYear(anchor), endDate: endOfYear(anchor) };
};

const SHORT_DATE = { month: 'short', day: 'numeric', year: 'numeric' };

/**
 * Compact chip label — "Jul 1 – Oct 27, 2026". Same-year windows
 * collapse the year onto the end date so the chip stays short.
 */
export const formatRangeChipLabel = (range) => {
  if (!range?.startDate || !range?.endDate) return '';
  const sameYear =
    range.startDate.getFullYear() === range.endDate.getFullYear();
  const a = range.startDate.toLocaleDateString(
    undefined,
    sameYear ? { month: 'short', day: 'numeric' } : SHORT_DATE
  );
  const b = range.endDate.toLocaleDateString(undefined, SHORT_DATE);
  return `${a} – ${b}`;
};
